import { appClient } from 'api/apiClient';
import { buildPageQuery, type Paginated } from 'api/v1Helpers';
import { normalizeQrCode, type ApiQrCode, type CreateQrPayload, type QrCode, type QrStatus } from 'types/qr';

/**
 * `basketId` scopes the page to one basket's codes. The deployed backend's list schema is
 * non-strict and does not yet read it (the filter is unmerged), so it is silently dropped
 * there — callers must not treat the returned page as basket-scoped on its own.
 */
export async function listQrCodes(
    parishId: string,
    opts?: { page?: number; pageSize?: number; status?: QrStatus; basketId?: string }
): Promise<Paginated<QrCode>> {
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 50, {
        status: opts?.status,
        basketId: opts?.basketId,
    });
    const page = await appClient.get<Paginated<ApiQrCode>>(`parishes/${parishId}/qr-codes?${params.toString()}`);
    return {
        items: (page.items || []).map(normalizeQrCode),
        total: page.total ?? 0,
        page: page.page ?? opts?.page ?? 1,
        pageSize: page.pageSize ?? opts?.pageSize ?? 50,
    };
}

/**
 * Create a QR code. Optional Idempotency-Key mirrors announcements (header may be
 * ignored until the Nest QR create endpoint gains server-side idempotency).
 */
export async function createQrCode(parishId: string, payload: CreateQrPayload, idempotencyKey?: string): Promise<QrCode> {
    const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
    const res = await appClient.post<ApiQrCode>(`parishes/${parishId}/qr-codes`, payload, headers);
    return normalizeQrCode(res);
}

/**
 * Stable key for ensure-default-ACTIVE create (same basket → same key across retries/tabs).
 *
 * Scoped by basket, not parish: a per-parish key would dedupe basket B's create against
 * basket A's the moment the backend starts honouring the header.
 *
 * NOTE: the backend does not read `Idempotency-Key` on QR create today (no header param on
 * the controller, no key storage in the service) — the header is inert, so this key provides
 * no de-duplication yet. Callers must not rely on it; see
 * docs/tickets/resolved/2026-08-12-BE-qr-create-idempotency.md.
 */
export function ensureBasketQrIdempotencyKey(parishId: string, basketId: string): string {
    return `ensure-basket-qr:${parishId}:${basketId}:default-active`;
}
